import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Globe, Check } from "lucide-react";
import { SUPPORTED_LNGS } from "@/i18n";

// Native labels for the 15 supported languages. Deliberately hard-coded
// (native names never translate) so we don't ship a "German → deutsch"
// round-trip through the JSON.
const NATIVE_LABELS = {
    en: "English", it: "Italiano", es: "Español", fr: "Français", de: "Deutsch",
    pt: "Português", nl: "Nederlands", pl: "Polski", tr: "Türkçe",
    ja: "日本語", "zh-CN": "简体中文", ru: "Русский", ar: "العربية",
    ko: "한국어", hi: "हिन्दी",
};

export default function LanguageSwitcher({ compact = false }) {
    const { i18n } = useTranslation();
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        const onClick = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", onClick);
        return () => document.removeEventListener("mousedown", onClick);
    }, []);

    // Special case: zh-CN is preserved (not split on '-')
    const raw = i18n.language || "en";
    const current = raw === "zh-CN" ? "zh-CN" : raw.split("-")[0];
    const setLang = (lng) => {
        i18n.changeLanguage(lng);
        setOpen(false);
    };

    return (
        <div className="relative" ref={ref}>
            <button
                data-testid="language-switcher"
                onClick={() => setOpen((o) => !o)}
                className="flex items-center gap-1.5 rounded-full border border-[var(--color-border)] bg-[var(--color-card)] px-3 py-1.5 text-xs uppercase tracking-widest text-text-secondary hover:border-[var(--color-primary)]/50 hover:text-text-main transition-colors"
                aria-label="Language"
            >
                <Globe className="h-3.5 w-3.5" />
                {!compact && <span className="tabular-nums">{current}</span>}
            </button>
            {open && (
                <div
                    data-testid="lang-switcher-menu"
                    className="absolute right-0 mt-2 w-48 max-h-[70vh] overflow-y-auto rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] shadow-lg overflow-hidden z-50"
                >
                    {SUPPORTED_LNGS.map((lng) => (
                        <button
                            key={lng}
                            data-testid={`lang-option-${lng}`}
                            onClick={() => setLang(lng)}
                            className={`flex w-full items-center justify-between gap-2 px-4 py-2.5 text-sm transition-colors ${
                                current === lng
                                    ? "text-text-main bg-[var(--color-panel-ai)]"
                                    : "text-text-secondary hover:bg-[var(--color-panel-ai)]/60 hover:text-text-main"
                            }`}
                        >
                            <span>{NATIVE_LABELS[lng] || lng}</span>
                            {current === lng && <Check className="h-3.5 w-3.5 text-[var(--color-primary)]" />}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

/**
 * IT nudge banner — shown once on first visit if navigator.language matches
 * Italian but current UI lang is EN. Dismiss-persistent via localStorage.
 */
export function ItalianNudge() {
    const { i18n, t } = useTranslation();
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        if (typeof window === "undefined") return;
        const dismissed = window.localStorage.getItem("glms_lang_nudge_dismissed");
        if (dismissed) return;
        const nav = (window.navigator.language || "").toLowerCase();
        const current = (i18n.language || "en").split("-")[0];
        if (nav.startsWith("it") && current !== "it") setVisible(true);
    }, [i18n.language]);

    if (!visible) return null;

    const accept = () => {
        i18n.changeLanguage("it");
        window.localStorage.setItem("glms_lang_nudge_dismissed", "1");
        setVisible(false);
    };
    const dismiss = () => {
        window.localStorage.setItem("glms_lang_nudge_dismissed", "1");
        setVisible(false);
    };

    return (
        <div
            data-testid="lang-nudge"
            className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 max-w-md w-[92%] rounded-xl border border-[var(--color-border)] bg-[var(--color-card)]/95 backdrop-blur-xl shadow-2xl px-4 py-3 flex items-center gap-3"
        >
            <Globe className="h-4 w-4 text-[var(--color-primary)] shrink-0" />
            <button
                data-testid="lang-nudge-accept"
                onClick={accept}
                className="text-sm font-medium hover:text-[var(--color-primary)] transition-colors"
            >
                {t("lang.switch_to_it")}
            </button>
            <button
                data-testid="lang-nudge-dismiss"
                onClick={dismiss}
                className="ml-auto text-xs text-text-secondary hover:text-text-main transition-colors"
            >
                {t("lang.dismiss")}
            </button>
        </div>
    );
}
